import React, { useContext, useState } from 'react';
import { ContractStatus, Phase3 } from '@indigo-ag/schema';
import { Theme, Input, Textarea, Button, useFlashMessage } from '@indigo-ag/component-module';

import i18next from 'core/i18n';
import { PhaseSection, createStylesHook, text } from 'shared';
import { Phase3Context } from 'features/brazil-grower/components/BrazilGrowerFinance/phases/Phase3';

const useStyles = createStylesHook<Theme>((theme: Theme) => ({
  card: {
    marginBottom: theme.spacing(3)
  }
}));

interface SignPhysicallySectionProps {
  phase3Info: Phase3;
}

export const SignPhysicallySection: React.FunctionComponent<SignPhysicallySectionProps> = ({ phase3Info }) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * State
   */
  const [trackingNumber, setTrackingNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [sendingToCommercial, setSendingToCommercial] = useState(false);

  /**
   * Context
   */
  const { updatePhase } = useContext(Phase3Context);

  const { addFlashMessage } = useFlashMessage();

  const handleTrackingNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setTrackingNumber(event.target.value);
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setNotes(event.target.value);
  };

  const handleSendToCommercial = () => {
    setSendingToCommercial(true);
    updatePhase({
      amendmentTrackingNumber: trackingNumber,
      amendmentSignedNotes: notes,
      contractStatus: ContractStatus.DRAFT_SENT_TO_COMMERCIAL
    })
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('successSendingAmendment')}`,
          title: '',
          variant: 'success'
        });
      })
      .finally(() => {
        setSendingToCommercial(false);
      });
  };

  return (
    <PhaseSection title={i18next.t('signingPhysicalDocuments')} classes={{ card: classes.card }}>
      <>
        <Input
          label={i18next.t('trackingNumber')}
          sizeVariant="slim"
          value={trackingNumber}
          onChange={handleTrackingNumberChange}
        />
        <Textarea
          label={`${text.firstLetterUppercase(i18next.t('instructions'))} (${i18next.t('optional')})`}
          rows={4}
          value={notes}
          onChange={handleNotesChange}
        />
        <div className="flex flex-space-btw">
          <div />
          <Button
            size="small"
            className="text-transform-capitalize"
            disabled={!trackingNumber}
            onClick={handleSendToCommercial}
            busy={sendingToCommercial}
          >
            {i18next.t('sentToCommercial')}
          </Button>
        </div>
      </>
    </PhaseSection>
  );
};
